import {ISectionItem} from "app/bookings/new/types/newBooking.types";
import {
  IScheduleInterval,
  ISectionMaxOcc,
  MaxOccupancyType
} from "app/app.types";
import moment from "moment";
import { IBooking } from "shared-types/index";

export const OVER_MAX = 'over-max'; // For Max occupancy - Yellow - CSS ClassName
export const BELOW_MAX = 'below-max'; // For nearly max occupancy - Light blue - CSS ClassName
export const DANGER_BUFFER = 2;

class MaxOccupancyService {

  __exposeForUnitTests() {
    return {
      _showMarkerColorSection: this.showMarkerColorSection
    }
  }

  getMaxOccupancyType(section: ISectionMaxOcc, intervalCovers: number): MaxOccupancyType {

    if (!section.maxOccupancy) {
      return MaxOccupancyType.none;
    }

    if (intervalCovers >= section.maxOccupancy) {
      return MaxOccupancyType.exceeded;
    }

    const DANGER_BUFFER = 2;

    if (intervalCovers < section.maxOccupancy - DANGER_BUFFER) {
      return MaxOccupancyType.safe;
    }

    // if more than safe amount, but less than exceeded amount return danger
    return MaxOccupancyType.danger;
  }

  private showMarkerColorSection(bookingInterval: IScheduleInterval, section: ISectionItem, pax = 0): string { // Pax will always be 0 for calls made from the timeline. Pax will be > 0 for calls made from new or edit booking
    let coversBySection = 0;
    let returnVal = '';
    if (!section || isNaN(section.maxOccupancy) || section.maxOccupancy === null) {
      return '';
    }
    const maxCovers = section.maxOccupancy || 0;
    const coversBySectionVal = bookingInterval.coversBySection?.get(section._id) || '';
    if (bookingInterval.coversBySection && pax === 0) { // For Timeline we have to check if coversBySection Exists.
      coversBySection = coversBySectionVal ? coversBySectionVal.sectionCovers : 0;
    } else if (pax > 0) { // This will be for new booking or edit booking. We are checking if coversBySection is available, then add with the booking pax or just have the pax
      coversBySection = coversBySectionVal ? coversBySectionVal.sectionCovers + pax : pax;
    }
    if (coversBySection > 0) {
      if (coversBySection >= maxCovers) {
        returnVal = OVER_MAX;
      } else if (coversBySection >= maxCovers - DANGER_BUFFER && coversBySection < maxCovers) {
        returnVal = BELOW_MAX;
      }
    }
    return returnVal;
  }

  /**
   *
   * @param booking
   * @param coversBookingIntervals
   * @param sections
   * @param isMultipleSection
   */

  checkForCovidMaxOccupancy(booking: IBooking, coversBookingIntervals: IScheduleInterval[], sections: ISectionItem[], isMultipleSection: boolean): string {

    let sectionOccupancyOverMax = '';

    if (!coversBookingIntervals) {
      return sectionOccupancyOverMax;
    }

    const sectionSelected: ISectionItem = sections && sections.find((section) => {
      return booking.tables.some(t => t.sectionId === section._id)
    }); // loop through all sections and booking tables to find if section exists

    if (!sectionSelected || isNaN(sectionSelected.maxOccupancy) || sectionSelected.maxOccupancy === null) {
      return '';
    }

    let coversForMaxOccupancy = booking.people;

    if (isMultipleSection) {
      const table = booking.tables.find((table) => {
        return sections.some(sec => sec._id === table.sectionId);
      }) // loop through all sections and booking tables to find the table present in that section
      coversForMaxOccupancy = table.capacity; // if multiple section exist then we calculate max occupancy based on table capacity instead of Booking Pax
    }

    if (coversForMaxOccupancy >= sectionSelected.maxOccupancy) {
      sectionOccupancyOverMax = OVER_MAX;
    } else {
      const bookingInterval: IScheduleInterval[] = coversBookingIntervals.filter((interval: { time: Date; }) => {
        const bookingStartTime = moment(booking.time);
        const bookingEndTime = moment(booking.time).add(booking.duration, 'minutes');
        const intervalTime = moment(interval.time);
        return intervalTime.isBetween(bookingStartTime, bookingEndTime, null, '[)'); // Getting range of all the bookingIntervals between the booking start time and endtime
      });

      bookingInterval.forEach((interval: IScheduleInterval) => {
        const sectionOccupancy = this.showMarkerColorSection(interval, sectionSelected, coversForMaxOccupancy);
        if (sectionOccupancy === OVER_MAX) {
          sectionOccupancyOverMax = sectionOccupancy;
        }
      });
    }
    return sectionOccupancyOverMax;
  }


  /***
   *
   * @param covers
   * @param section
   * @Returns string - maxOccupancy type
   */

  getTypeOfMarker(covers: number, section: ISectionMaxOcc): string {
    let returnVal;
    const maxCovers = section?.maxOccupancy;
    if (!isNaN(maxCovers) && maxCovers !== null && covers > 0 && maxCovers >= 0) {
      if (covers >= maxCovers) {
        returnVal = OVER_MAX;
      } else if (covers >= maxCovers - DANGER_BUFFER && covers < maxCovers) {
        returnVal = BELOW_MAX;
      }
    }
    return returnVal;
  }

}

const instance = new MaxOccupancyService();
export default instance as MaxOccupancyService;
